import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  useToastStack,
  TOAST_BASE_OFFSET,
  TOAST_BASE_OFFSET_WITH_COOKIE_BANNER,
  TOAST_STACK_SLOT_HEIGHT
} from "../context/ToastStackContext";

const COOKIE_CONSENT_KEY = "virsa_cookie_consent";
let nextUndoToastId = 0;

// Like Toast, but with an action — used for soft-delete: the caller hides
// the item immediately and only performs the real delete once this expires
// without being undone.
export default function UndoToast({ text, onUndo, onExpire, duration = 5000 }) {
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  // Shares Toast.jsx's stack registry — deleting a comment (this) while
  // ALSO liking something (a plain Toast) used to draw both at overlapping
  // positions, since each toast type tracked its own stacking independently.
  // The id space is shared with Toast.jsx's own counter by prefixing it, so
  // the two never collide despite each keeping its own counter.
  const [id] = useState(() => `undo-${++nextUndoToastId}`);
  const stack = useToastStack();

  useEffect(() => {
    stack?.register(id);
    return () => stack?.unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read once on mount — sits higher while the cookie banner is still up.
  // Uses the exact same base values as Toast.jsx (see ToastStackContext.jsx)
  // rather than its own independently-tuned numbers, so the two line up
  // correctly when they land in the same shared stack together.
  const [clearCookieBanner] = useState(() => {
    try {
      return localStorage.getItem(COOKIE_CONSENT_KEY) !== "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const t = setTimeout(() => onExpireRef.current(), duration);
    return () => clearTimeout(t);
  }, [duration]);

  const index = stack ? stack.ids.indexOf(id) : -1;
  const stackOffset = index > 0 ? index * TOAST_STACK_SLOT_HEIGHT : 0;
  const base = clearCookieBanner ? TOAST_BASE_OFFSET_WITH_COOKIE_BANNER : TOAST_BASE_OFFSET;

  // See Toast.jsx — portaled for the same reason: rendered in place, any
  // transformed/filtered ancestor between this and the page root would hijack
  // position:fixed to be relative to that ancestor instead of the viewport.
  return createPortal(
    <div
      style={{
        ...toast,
        bottom: `calc(${base}px + env(safe-area-inset-bottom) + ${stackOffset}px)`
      }}
    >
      <span>{text}</span>
      <button style={undoBtn} onClick={onUndo}>
        Undo
      </button>
    </div>,
    document.body
  );
}

const toast = {
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  gap: 14,
  background: "#000",
  color: "#fff",
  padding: "10px 14px 10px 16px",
  borderRadius: 10,
  fontSize: 14,
  zIndex: 9999,
  // See Toast.jsx — without this, an UndoToast sharing the stack with a
  // plain Toast snaps straight to its new slot when the other one dismisses
  // first, instead of sliding down like Toast.jsx's own does.
  transition: "bottom 0.2s ease",
  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
};

const undoBtn = {
  background: "none",
  border: "none",
  color: "#7fc8ff",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
  padding: 0,
  flexShrink: 0,
};
